import {
  TICKET_LIST_SCOPE_STATES,
  isScopeView,
  ticketQueueQuerySchema,
  type TicketListScope,
  type TicketListView,
  type TicketQueueFilters,
  type TicketQueueQuery,
} from '@easyhelp/shared';

/**
 * Stato della coda o dell'archivio nell'URL (search params): vista, ordinamento, filtri, ricerca e
 * pagina. Ricaricando la pagina, aprendo e chiudendo un ticket o tornando indietro la lista resta
 * com'era. L'ambito non sta nella query string: è il percorso (`/` o `/archivio`, #123).
 */
export type QueueParams = Pick<TicketQueueQuery, 'sort' | 'page'> & {
  view: TicketListView;
} & Partial<TicketQueueFilters>;

/** Chiavi della lista nell'URL: le stesse della query string dell'API, ambito escluso. */
const KEYS = ['view', 'sort', 'committente', 'priority', 'state', 'assignee', 'reply', 'q', 'page'] as const;

/** Chiavi dei filtri e della ricerca: quelle che la ricerca incrociata porta nell'altro ambito. */
const FILTER_KEYS = ['committente', 'priority', 'state', 'assignee', 'reply', 'q'] as const;

/** La lista senza filtri: vista Tutti, per apertura, prima pagina. */
export const DEFAULT_QUEUE_PARAMS: QueueParams = { view: 'tutti', sort: 'apertura', page: 1 };

/** Percorso di ciascun ambito: la coda è la home, l'archivio una rotta propria (#123). */
export const SCOPE_PATHS: Record<TicketListScope, string> = { coda: '/', archivio: '/archivio' };

/**
 * Legge la lista dall'URL con lo schema dell'API, campo per campo: un valore non valido (link vecchio
 * o scritto a mano) è ignorato da solo, senza perdere gli altri filtri. Anche una vista o uno stato di
 * un altro ambito sono ignorati: l'archivio non ha «Allo sviluppo», la coda non ha «Chiuso». Il filtro
 * «Risposta ricevuta» (#122) è solo della coda: nell'archivio nessun ticket ce l'ha.
 */
export function readQueueParams(search: URLSearchParams, scope: TicketListScope = 'coda'): QueueParams {
  const params: QueueParams = { ...DEFAULT_QUEUE_PARAMS };
  for (const key of KEYS) {
    const value = search.get(key);
    if (value === null) continue;
    const result = ticketQueueQuerySchema.shape[key].safeParse(value);
    if (!result.success || result.data === undefined) continue;
    if (key === 'view' && !isScopeView(scope, String(result.data))) continue;
    if (key === 'state' && !TICKET_LIST_SCOPE_STATES[scope].includes(result.data as never)) continue;
    if (key === 'reply' && scope !== 'coda') continue;
    Object.assign(params, { [key]: result.data });
  }
  return params;
}

/** Scrive la lista nell'URL, omettendo i valori di default. Altri parametri non riguardano la lista. */
export function writeQueueParams(params: QueueParams): URLSearchParams {
  const search = new URLSearchParams();
  if (params.view !== DEFAULT_QUEUE_PARAMS.view) search.set('view', params.view);
  if (params.sort !== DEFAULT_QUEUE_PARAMS.sort) search.set('sort', params.sort);
  for (const key of FILTER_KEYS) {
    const value = params[key];
    if (value) search.set(key, value);
  }
  if (params.page !== DEFAULT_QUEUE_PARAMS.page) search.set('page', String(params.page));
  return search;
}

/**
 * Vero se vista, filtri o ricerca restringono la lista: è quando «Azzera filtri» ha senso.
 * L'ordinamento non restringe nulla, quindi non conta.
 */
export function hasActiveFilters(params: QueueParams): boolean {
  return params.view !== DEFAULT_QUEUE_PARAMS.view || hasFiltersOrSearch(params);
}

/** Vero se filtri o ricerca sono attivi, vista esclusa: è quando la ricerca incrociata ha senso. */
export function hasFiltersOrSearch(params: QueueParams): boolean {
  return FILTER_KEYS.some((key) => Boolean(params[key]));
}

/**
 * Query string dell'altro ambito con la stessa ricerca, gli stessi filtri e lo stesso ordinamento
 * (#123): vista e pagina ripartono da capo, lo stato no perché non esiste nell'altro ambito, né
 * «Risposta ricevuta» (#122), che vale solo in coda.
 */
export function crossScopeSearch(params: QueueParams): string {
  const search = writeQueueParams({
    ...DEFAULT_QUEUE_PARAMS,
    ...params,
    view: 'tutti',
    state: undefined,
    reply: undefined,
    page: 1,
  });
  const query = search.toString();
  return query ? `?${query}` : '';
}

/** Percorso del dettaglio di un ticket sopra la coda o l'archivio, con i filtri nella query string. */
export function ticketPath(code: string, search: string, scope: TicketListScope = 'coda') {
  const base = scope === 'coda' ? '' : SCOPE_PATHS[scope];
  return { pathname: `${base}/ticket/${encodeURIComponent(code)}`, search };
}

/** {@link ticketPath} come `href`, per la `DataTable` (`getRowHref` vuole una stringa). */
export function ticketHref(code: string, search: string, scope: TicketListScope = 'coda'): string {
  const { pathname, search: query } = ticketPath(code, search, scope);
  return `${pathname}${query}`;
}

/** Percorso della coda o dell'archivio con gli stessi filtri: chiudere il dettaglio non li perde. */
export function queuePath(search: string, scope: TicketListScope = 'coda') {
  return { pathname: SCOPE_PATHS[scope], search };
}

/** {@link queuePath} come `href`, per i link che vogliono una stringa (il breadcrumb della shell). */
export function queueHref(search: string, scope: TicketListScope = 'coda'): string {
  const { pathname, search: query } = queuePath(search, scope);
  return `${pathname}${query}`;
}

/**
 * Viste della coda tolte con l'archivio (#123): un link salvato prima porta dove vive oggi quella
 * lista. «Aperti» è la coda stessa; «Risolti» è una vista dell'archivio.
 */
const LEGACY_QUEUE_VIEWS: Record<string, { scope: TicketListScope; view: TicketListView }> = {
  aperti: { scope: 'coda', view: 'tutti' },
  risolti: { scope: 'archivio', view: 'risolti' },
};

/**
 * Dove porta un vecchio link della coda con `?view=aperti` o `?view=risolti` (#123), con gli altri
 * filtri e, se c'era, il ticket aperto; `null` se il link non ha una vista tolta. Uno stato che
 * nell'ambito di arrivo non esiste è lasciato cadere, come farebbe la lettura dell'URL.
 */
export function legacyQueueRedirect(
  pathname: string,
  search: URLSearchParams
): { pathname: string; search: string } | null {
  const target = LEGACY_QUEUE_VIEWS[search.get('view') ?? ''];
  if (!target) return null;
  const rest = new URLSearchParams(search);
  rest.delete('view');
  const params = { ...readQueueParams(rest, target.scope), view: target.view };
  const query = writeQueueParams(params).toString();
  const ticket = /^\/ticket\/[^/]+$/.exec(pathname)?.[0] ?? '';
  const base = target.scope === 'coda' ? '' : SCOPE_PATHS[target.scope];
  return { pathname: `${base}${ticket}` || '/', search: query ? `?${query}` : '' };
}
